import React, { useState } from "react";

// Data Array
const places = [
    {
        id: 1,
        name: "Shore Temple",
        shortTag: "UNESCO Heritage",
        distance: "Near beach",
        imageUrl: require("../assets/img/aqua-mahabalipuram/1.jpg"),
        description:
            "An iconic 8th-century structural temple built by the Pallavas. Overlooks the Bay of Bengal — especially beautiful around sunrise/sunset.",
    },
    {
        id: 2,
        name: "Pancha Rathas",
        shortTag: "Monolithic shrines",
        distance: "1.5 km from shore",
        imageUrl: require("../assets/img/aqua-mahabalipuram/2.jpg"),
        description:
            "A collection of five monolithic rock-cut temples, each called a “ratha” (chariot) and named after the five Pandava brothers + Draupadi.",
    },
    {
        id: 3,
        name: "Arjuna's Penance",
        shortTag: "Rock relief",
        distance: "Central town",
        imageUrl: require("../assets/img/aqua-mahabalipuram/3.webp"),
        description:
            "One of the world's largest rock-cut reliefs, depicting scenes from the Hindu epic Mahabharata.",
    },
    {
        id: 4,
        name: "Mahishasuramardini Cave",
        shortTag: "Cave temple",
        distance: "Hilltop viewpoint",
        imageUrl: require("../assets/img/aqua-mahabalipuram/4.jpg"),
        description:
            "A 7th-century cave with stunning carvings of the goddess Durga. Also has scenes of Vishnu in reclining posture (Anantasayana).",
    },
    {
        id: 5,
        name: "Krishna Butter Ball",
        shortTag: "Carving gallery",
        distance: "Walkable from shore",
        imageUrl: require("../assets/img/aqua-mahabalipuram/5.jpg"),
        description:
            "A large, natural boulder resting on a hillside that appears to defy gravity.",
    },
    {
        id: 6,
        name: "Tiger Cave",
        shortTag: "Quiet picnic spot",
        distance: "5 km from town",
        imageUrl: require("../assets/img/aqua-mahabalipuram/6.jpg"),
        description:
            "An 8th-century cave temple with relief carvings of a lion and tiger.",
    },
    {
        id: 7,
        name: "India Seashell Museum",
        shortTag: "Asia’s largest seashell museum",
        distance: "3 km from town",
        imageUrl: require("../assets/img/aqua-mahabalipuram/7.jpg"),
        description:
            "The largest seashell museum in Asia, showcasing 40,000+ seashells, fossils, pearls, and marine exhibits.",
    },
];

const MahabalipuramPlaces = () => {
    return (
        <section className="py-12 px-4 sm:px-6 lg:py-16 lg:px-8 bg-slate-50">
            <div className="max-w-7xl mx-auto">
                {/* Heading Section */}
                <div className="text-center mb-10 sm:mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 mb-4 shadow-sm">
                        <span className="h-2 w-2 rounded-full bg-sky-600 animate-pulse" />
                        <span className="text-[10px] sm:text-xs font-bold tracking-wider text-sky-700 uppercase">
                            Travel Guide
                        </span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                        Explore Mahabalipuram
                    </h2>

                    <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                        Discover UNESCO World Heritage sites, ancient rock-cut caves, and
                        stunning coastal landmarks.
                    </p>
                </div>

                {/* Responsive Grid */}
                <div className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {places.map((place) => (
                        <Card key={place.id} place={place} />
                    ))}
                </div>
            </div>
        </section>
    );
};

const Card = ({ place }) => {
    const [imgError, setImgError] = useState(false);

    return (
        <div className="group relative flex flex-col h-full overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-in-out">
            {/* Image Container */}
            <div className="relative h-56 sm:h-64 overflow-hidden bg-slate-200 shrink-0">
                {!imgError ? (
                    <img
                        src={place.imageUrl}
                        alt={place.name}
                        onError={() => setImgError(true)}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                ) : (
                    <div className="h-full w-full flex items-center justify-center bg-sky-50 text-sky-300">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-12 h-12"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                            />
                        </svg>
                    </div>
                )}

                {/* Gradient Overlay for Text Readability if needed, mostly style */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-60"></div>

                {/* Distance Badge */}
                <div className="absolute top-4 right-4 z-10">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-700 shadow-sm backdrop-blur-md border border-slate-100/50">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-3 h-3 text-sky-500"
                        >
                            <path
                                fillRule="evenodd"
                                d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.493 1.698 5.988 3.355 7.72.829.799 1.654 1.38 2.274 1.765a5.741 5.741 0 001.056.583zM10 13a4 4 0 100-8 4 4 0 000 8z"
                                clipRule="evenodd"
                            />
                        </svg>
                        {place.distance}
                    </span>
                </div>
            </div>

            {/* Content Body - flex-1 ensures footer stays at bottom */}
            <div className="flex flex-1 flex-col p-3">
                {/* Tag */}
                {place.shortTag && (
                    <div className="mb-2">
                        <span className="inline-block rounded-md bg-sky-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-sky-600">
                            {place.shortTag}
                        </span>
                    </div>
                )}

                {/* Title */}
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-sky-700 transition-colors">
                    {place.name}
                </h3>

                {/* Description - limit to 3 lines */}
                <p className="text-sm leading-relaxed text-slate-600 line-clamp-3 mb-4">
                    {place.description}
                </p>

                {/* Spacer to push footer down */}
                <div className="flex-grow" />

                {/* Bottom Action Area */}
                <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        Open now
                    </span>

                    <button className="group/btn inline-flex items-center gap-1 text-sm font-bold text-sky-600 hover:text-sky-700 transition-all">
                        Explore
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1"
                        >
                            <path
                                fillRule="evenodd"
                                d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.5a.75.75 0 010 1.08l-5.5 5.5a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MahabalipuramPlaces;